import React from "react";
import type { ReactNode, ErrorInfo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

interface ErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  resetKey?: string | number;
  navigate?: NavigateFunction;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

import { useNavigate, useLocation, NavigateFunction } from "react-router-dom";

export default function ErrorBoundaryWithNav(props: any) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <ErrorBoundary
      {...props}
      navigate={navigate}
      resetKey={location.pathname}
    />
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, errorId: `${Date.now()}` };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // preserve existing console usage pattern
    console.error("Error caught by ErrorBoundary:", error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      this.props.resetKey !== undefined &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.reset();
    }
  }

  reset() {
    this.setState({ hasError: false, error: null, errorId: null });
    if (this.props.onReset) this.props.onReset();
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children as React.ReactElement;
    }

    const message = this.state.error?.message ?? "An unexpected error occurred";
    const details = this?.state?.error?.stack || "";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // width: "100%",
          // height: "100%",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 920,
            width: "100%",
            borderRadius: 2,
            p: 3,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="h6" color="error" sx={{ fontWeight: 700 }}>
                Something went wrong
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                The page encountered an error and cannot be displayed right now.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Error
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 640,
                  }}
                  title={message}
                >
                  {message}
                </Typography>
                {details && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 1,
                      maxHeight: 120,
                      overflow: "auto",
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    {details}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.reset}
                    size="medium"
                  >
                    Try again
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => globalThis.location.reload()}
                    size="medium"
                  >
                    Reload app
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => this.props?.navigate?.("/")}
                  size="medium"
                >
                  Home
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Error ID:</strong> {this.state.errorId}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }
}
